/**
 * pages/CompartmentAnalysis.jsx
 * 
 * Shows two views:
 * 1. Tabs per compartment — drill into each compartment's services
 * 2. Summary — side-by-side Primary vs DR comparison table
 */
import PrereqBanner from "../components/PrereqBanner";
import { useState, useEffect, useCallback } from "react";
import * as api from "../api/client";
import {
  Card, Btn, SectionTitle, Badge, ErrorBanner,
  Spinner, Empty, Table, TR, TD, KPICard, Select,
} from "../components/UI";

const fmtINR = n => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits:2, maximumFractionDigits:2 })}`;
const pct    = (a, b) => b > 0 ? `${((a/b)*100).toFixed(1)}%` : "—";

// Classify compartment name to category
function classifyComp(name) {
  const n = (name || "").toLowerCase();
  if (n.endsWith("-db") || n.endsWith("-database")) return { cat:"database", color:"#7C3AED", bg:"#F5F3FF" };
  if (n.endsWith("-app"))                            return { cat:"app",      color:"#2563EB", bg:"#EFF6FF" };
  if (n.endsWith("-web"))                            return { cat:"web",      color:"#0F6E56", bg:"#F0FDF4" };
  if (n.includes("network"))                        return { cat:"network",  color:"#D97706", bg:"#FFFBEB" };
  if (n.includes("security"))                       return { cat:"security", color:"#DC2626", bg:"#FEF2F2" };
  if (n.includes("backup"))                         return { cat:"backup",   color:"#0369A1", bg:"#F0F9FF" };
  return { cat:"general", color:"#6B7280", bg:"#F9FAFB" };
}

function downloadCSV(filename, headers, rows) {
  const e = v => { if(v==null)return""; const s=String(v); return s.includes(",")||s.includes('"')||s.includes("\n")?`"${s.replace(/"/g,'""')}"`:s; };
  const lines = [headers.map(e).join(","), ...rows.map(r=>r.map(e).join(","))].join("\n");
  const blob  = new Blob(["\uFEFF"+lines], {type:"text/csv;charset=utf-8;"});
  const url   = URL.createObjectURL(blob);
  const a     = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

export default function CompartmentAnalysis({ clientId, clientName }) {
  const [months, setMonths]         = useState([]);
  const [selected, setSelected]     = useState("");
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [data, setData]             = useState(null);
  const [activeTab, setActiveTab]   = useState("summary");
  const [activeComp, setActiveComp] = useState(null);

  // Load available months
  useEffect(() => {
    api.analysis.availableMonths(clientId)
      .then(r => setMonths(r.months || []))
      .catch(e => setError(e.message));
  }, [clientId]);

  // Load compartment data when month changes
  const loadData = useCallback(async (month) => {
    if (!month) return;
    setLoading(true); setError("");
    try {
      const r = await api.analysis.compartments(clientId, month);
      setData(r);
      setActiveComp(null);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadData(selected); }, [selected]);

  const monthOptions = months.map(m => ({ value:m, label:m }));

  // Split compartments by region
  const mumbaiComps    = (data?.compartments || []).filter(c => c.environment === "Primary").filter(c => c.total > 0);
  const hyderabadComps = (data?.compartments || []).filter(c => c.environment === "DR").filter(c => c.total > 0);

  // All unique compartment names across both regions
  const allCompNames = [...new Set([
    ...mumbaiComps.map(c => c.compartment),
    ...hyderabadComps.map(c => c.compartment),
  ])].sort();

  const downloadSummary = () => {
    if (!data) return;
    const headers = ["Month","Compartment","Category","Environment","Region","Service","Cost (INR)"];
    const rows = [];
    (data.compartments || []).forEach(c => {
      const cls = classifyComp(c.compartment);
      Object.entries(c.services || {}).forEach(([svc, cost]) => {
        if (cost > 0) rows.push([
          selected, c.compartment, cls.cat, c.environment, c.region, svc, cost.toFixed(2)
        ]);
      });
    });
    downloadCSV(`${clientName}_Compartment_${selected}.csv`, headers, rows);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <SectionTitle
          children="Compartment analysis"
          sub="Cost breakdown by OCI compartment — Primary (Mumbai) vs DR (Hyderabad)"
        />
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          {data && <Btn onClick={downloadSummary} variant="secondary" size="sm">↓ Download CSV</Btn>}
          <Select
            label=""
            options={[{value:"",label:"Select month…"}, ...monthOptions]}
            value={selected}
            onChange={e => setSelected(e.target.value)}
            style={{ minWidth:160 }}
          />
        </div>
      </div>

      <ErrorBanner message={error} onDismiss={() => setError("")}/>

      {!selected && <Empty icon="🏢" title="Select a month" sub="Choose a billing month to see compartment breakdown"/>}
      {selected && loading && <div style={{ textAlign:"center", padding:48 }}><Spinner size={28}/></div>}

      {data && !loading && (
        <>
          {/* Region KPI cards */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:14, marginBottom:24 }}>
            <KPICard label="Mumbai (Primary)"   value={fmtINR(data.mumbai_total)}    accent="#2563EB"/>
            <KPICard label="Hyderabad (DR)"     value={fmtINR(data.hyderabad_total)} accent="#7C3AED"/>
            <KPICard label="Grand total"        value={fmtINR(data.grand_total)}      accent="#D97706"/>
            <KPICard label="Compartments"       value={data.compartment_count}        accent="#16A34A"/>
          </div>

          {/* View tabs */}
          <div style={{ display:"flex", gap:4, marginBottom:20, borderBottom:"2px solid #F3F4F6" }}>
            {[
              { id:"summary",     label:"📊 Primary vs DR comparison" },
              { id:"compartments",label:"🏢 Per compartment detail"   },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                padding:"8px 18px", border:"none", cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600,
                background:"transparent",
                color: activeTab===tab.id ? "#2563EB" : "#9CA3AF",
                borderBottom: activeTab===tab.id ? "2px solid #2563EB" : "2px solid transparent",
                marginBottom:-2,
              }}>{tab.label}</button>
            ))}
          </div>

          {/* ── TAB 1: Primary vs DR comparison ─────────────────────────── */}
          {activeTab === "summary" && (
            <Card>
              <SectionTitle children="Primary vs DR — by compartment" sub="Side-by-side cost comparison per compartment"/>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E2E8F0" }}>
                    <th style={th}>Compartment</th>
                    <th style={th}>Category</th>
                    <th style={{...th, textAlign:"right", color:"#2563EB"}}>Primary (Mumbai)</th>
                    <th style={{...th, textAlign:"right", color:"#7C3AED"}}>DR (Hyderabad)</th>
                    <th style={{...th, textAlign:"right"}}>Total</th>
                    <th style={{...th, textAlign:"right"}}>DR ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {allCompNames.map((compName, i) => {
                    const pr  = mumbaiComps.find(c => c.compartment === compName);
                    const dr  = hyderabadComps.find(c => c.compartment === compName);
                    const cls = classifyComp(compName);
                    const prTotal  = pr?.total || 0;
                    const drTotal  = dr?.total || 0;
                    const rowTotal = prTotal + drTotal;
                    if (rowTotal < 1) return null;
                    const drRatio  = rowTotal > 0 ? ((drTotal / rowTotal) * 100).toFixed(1) : 0;

                    return (
                      <tr key={compName} style={{ borderBottom:"1px solid #F3F4F6", background: i%2===0?"#fff":"#FAFAFA", cursor:"pointer" }}
                          onClick={() => { setActiveComp(compName); setActiveTab("compartments"); }}>
                        <td style={{ padding:"10px 12px", fontWeight:600, color:"#111827" }}>
                          {compName}
                        </td>
                        <td style={{ padding:"10px 12px" }}>
                          <span style={{ background:cls.bg, color:cls.color, padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:700, textTransform:"uppercase" }}>
                            {cls.cat}
                          </span>
                        </td>
                        <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:600, color:"#2563EB" }}>
                          {prTotal > 0 ? fmtINR(prTotal) : <span style={{color:"#D1D5DB"}}>—</span>}
                        </td>
                        <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:600, color:"#7C3AED" }}>
                          {drTotal > 0 ? fmtINR(drTotal) : <span style={{color:"#D1D5DB"}}>—</span>}
                        </td>
                        <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:700, color:"#111827" }}>
                          {fmtINR(rowTotal)}
                        </td>
                        <td style={{ padding:"10px 12px", textAlign:"right" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"flex-end" }}>
                            <div style={{ width:60, height:6, background:"#F3F4F6", borderRadius:3, overflow:"hidden" }}>
                              <div style={{ width:`${drRatio}%`, height:"100%", background:"#7C3AED", borderRadius:3 }}/>
                            </div>
                            <span style={{ fontSize:12, color:"#6B7280", width:36, textAlign:"right" }}>{drRatio}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr style={{ borderTop:"2px solid #E2E8F0", background:"#F8FAFC" }}>
                    <td style={{ padding:"10px 12px", fontWeight:800, color:"#111827" }}>TOTAL</td>
                    <td/>
                    <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:800, color:"#2563EB" }}>{fmtINR(data.mumbai_total)}</td>
                    <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:800, color:"#7C3AED" }}>{fmtINR(data.hyderabad_total)}</td>
                    <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:800, color:"#111827" }}>{fmtINR(data.grand_total)}</td>
                    <td style={{ padding:"10px 12px", textAlign:"right", color:"#6B7280", fontSize:12 }}>
                      {((data.hyderabad_total / data.grand_total)*100).toFixed(1)}%
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop:12, fontSize:12, color:"#9CA3AF" }}>
                Click any row to drill into that compartment's service breakdown →
              </div>
            </Card>
          )}

          {/* ── TAB 2: Per compartment detail ────────────────────────────── */}
          {activeTab === "compartments" && (
            <div>
              {/* Compartment selector tabs */}
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:20 }}>
                {(data.compartments || [])
                  .filter(c => c.total > 0)
                  .sort((a,b) => b.total - a.total)
                  .map(c => {
                    const cls   = classifyComp(c.compartment);
                    const isEnv = c.environment;
                    const label = `${c.compartment} (${c.environment === "Primary" ? "PR" : "DR"})`;
                    const isActive = activeComp === c.compartment + c.environment;
                    return (
                      <button key={label}
                        onClick={() => setActiveComp(isActive ? null : c.compartment + c.environment)}
                        style={{
                          padding:"5px 12px", borderRadius:6, border:`1px solid ${isActive ? cls.color : "#E5E7EB"}`,
                          background: isActive ? cls.bg : "#fff",
                          color: isActive ? cls.color : "#6B7280",
                          fontSize:12, fontWeight:600, cursor:"pointer",
                          transition:"all 0.15s",
                        }}>
                        {label}
                        <span style={{ marginLeft:6, color:"#9CA3AF", fontWeight:400 }}>
                          {fmtINR(c.total)}
                        </span>
                      </button>
                    );
                  })
                }
              </div>

              {/* Selected compartment detail */}
              {activeComp && (() => {
                const comp = (data.compartments || []).find(
                  c => (c.compartment + c.environment) === activeComp
                );
                if (!comp) return null;
                const cls      = classifyComp(comp.compartment);
                const services = Object.entries(comp.services || {})
                  .filter(([,v]) => v > 0)
                  .sort((a,b) => b[1]-a[1]);
                const total = services.reduce((s,[,v]) => s+v, 0);

                return (
                  <Card style={{ borderTop:`3px solid ${cls.color}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:16, color:"#111827" }}>{comp.compartment}</div>
                        <div style={{ display:"flex", gap:8, marginTop:4 }}>
                          <span style={{ background:cls.bg, color:cls.color, padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:700 }}>
                            {cls.cat.toUpperCase()}
                          </span>
                          <span style={{
                            background: comp.environment==="Primary" ? "#EFF6FF" : "#F5F3FF",
                            color:      comp.environment==="Primary" ? "#2563EB" : "#7C3AED",
                            padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:700
                          }}>
                            {comp.environment === "Primary" ? "Mumbai (Primary)" : "Hyderabad (DR)"}
                          </span>
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:11, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.05em" }}>Total cost</div>
                        <div style={{ fontSize:22, fontWeight:800, color:cls.color }}>{fmtINR(total)}</div>
                        <div style={{ fontSize:11, color:"#9CA3AF" }}>
                          {((total / data.grand_total)*100).toFixed(1)}% of grand total
                        </div>
                      </div>
                    </div>

                    {/* Service breakdown */}
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                      <thead>
                        <tr style={{ background:"#F8FAFC", borderBottom:"1px solid #E2E8F0" }}>
                          <th style={th}>Service</th>
                          <th style={{...th, textAlign:"right"}}>Cost (INR)</th>
                          <th style={{...th, textAlign:"right"}}>% of compartment</th>
                          <th style={th}>Bar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {services.map(([svc, cost], i) => {
                          const barPct = total > 0 ? (cost / total) * 100 : 0;
                          return (
                            <tr key={svc} style={{ borderBottom:"1px solid #F9FAFB", background: i%2===0?"#fff":"#FAFAFA" }}>
                              <td style={{ padding:"8px 12px", fontWeight:500, color:"#374151", fontFamily:"monospace", fontSize:12 }}>
                                {svc}
                              </td>
                              <td style={{ padding:"8px 12px", textAlign:"right", fontWeight:600, color:"#111827" }}>
                                {fmtINR(cost)}
                              </td>
                              <td style={{ padding:"8px 12px", textAlign:"right", color:"#6B7280", fontSize:12 }}>
                                {barPct.toFixed(1)}%
                              </td>
                              <td style={{ padding:"8px 12px", width:120 }}>
                                <div style={{ background:"#F3F4F6", borderRadius:3, height:8, overflow:"hidden" }}>
                                  <div style={{ width:`${barPct}%`, height:"100%", background:cls.color, borderRadius:3 }}/>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Card>
                );
              })()}

              {/* No compartment selected */}
              {!activeComp && (
                <Card style={{ background:"#F8FAFC" }}>
                  <div style={{ textAlign:"center", padding:"32px 0", color:"#9CA3AF" }}>
                    <div style={{ fontSize:32, marginBottom:8 }}>🏢</div>
                    <div style={{ fontSize:14, fontWeight:600 }}>Select a compartment above to see its service breakdown</div>
                  </div>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const th = {
  padding: "10px 12px",
  textAlign: "left",
  fontWeight: 600,
  color: "#374151",
  fontSize: 12,
  whiteSpace: "nowrap",
};
